import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useToast } from './ui/use-toast';
import axios from 'axios';
import {
  ArrowLeft,
  MapPin,
  Clock,
  Navigation,
  User,
  MessageSquare,
  Camera,
  AlertCircle,
  CheckCircle,
  Calendar,
  TrendingUp
} from 'lucide-react';

const DetalhesViagemPage = () => {
  const { viagemId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [viagem, setViagem] = useState(null);
  const [carregando, setCarregando] = useState(true);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const GOOGLE_MAPS_KEY = 'AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8';

  // Carregar dados da viagem
  useEffect(() => {
    carregarViagem();
  }, [viagemId]);

  const carregarViagem = async () => {
    setCarregando(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/viagens/${viagemId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setViagem(response.data);
    } catch (error) {
      console.error('Erro ao carregar viagem:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar detalhes da viagem",
        variant: "destructive"
      });
    } finally {
      setCarregando(false);
    }
  };

  // Formatar data
  const formatarData = (dataISO) => {
    if (!dataISO) return '-';
    const data = new Date(dataISO);
    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Formatar tempo
  const formatarTempo = (minutos) => {
    if (!minutos) return '-';
    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;
    return `${horas}h ${mins}min`;
  };

  // Gerar URL do mapa com trajeto
  const gerarURLMapa = () => {
    if (!viagem || !viagem.trajeto || viagem.trajeto.length < 2) return null;
    
    // Ponto inicial e final
    const pontoInicial = viagem.trajeto[0];
    const pontoFinal = viagem.trajeto[viagem.trajeto.length - 1];
    
    // Montar waypoints (máximo 25 pontos para não sobrecarregar)
    const step = Math.max(1, Math.floor(viagem.trajeto.length / 25));
    const waypoints = viagem.trajeto
      .filter((_, index) => index % step === 0 && index > 0 && index < viagem.trajeto.length - 1)
      .map(p => `${p.latitude},${p.longitude}`)
      .join('|');
    
    // URL do Google Maps Static API com polyline
    let url = `https://maps.googleapis.com/maps/api/staticmap?`;
    url += `size=800x400&`;
    url += `markers=color:green|label:I|${pontoInicial.latitude},${pontoInicial.longitude}&`;
    url += `markers=color:red|label:F|${pontoFinal.latitude},${pontoFinal.longitude}&`;
    
    // Adicionar visitas no mapa
    if (viagem.visitas && viagem.visitas.length > 0) {
      viagem.visitas.forEach((visita, index) => {
        url += `markers=color:blue|label:${index + 1}|${visita.latitude_chegada},${visita.longitude_chegada}&`;
      });
    }
    
    // Adicionar path (trajeto)
    if (waypoints) {
      url += `path=color:0x0000ff80|weight:3|${pontoInicial.latitude},${pontoInicial.longitude}|${waypoints}|${pontoFinal.latitude},${pontoFinal.longitude}&`;
    }
    
    url += `key=${GOOGLE_MAPS_KEY}`;
    
    return url;
  };

  if (carregando) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-600 mt-4">Carregando detalhes...</p>
        </div>
      </div>
    );
  }

  if (!viagem) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 mx-auto text-red-600 mb-4" />
          <p className="text-gray-600">Viagem não encontrada</p>
          <Button onClick={() => navigate('/historico-viagens')} className="mt-4">
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  const mapaURL = gerarURLMapa();

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/historico-viagens')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Detalhes da Viagem</h1>
            <p className="text-gray-600 mt-1">{viagem.funcionario_nome}</p>
          </div>
        </div>
        
        <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
          viagem.status === 'aprovada' ? 'bg-green-100 text-green-700' :
          viagem.status === 'finalizada' ? 'bg-blue-100 text-blue-700' :
          viagem.status === 'em_andamento' ? 'bg-yellow-100 text-yellow-700' :
          'bg-gray-100 text-gray-700'
        }`}>
          {viagem.status === 'aprovada' ? 'Aprovada' :
           viagem.status === 'finalizada' ? 'Finalizada' :
           viagem.status === 'em_andamento' ? 'Em Andamento' : viagem.status}
        </span>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">KM Rodados</p>
                <p className="text-3xl font-bold text-blue-600">{viagem.km_rodados?.toFixed(1)}</p>
              </div>
              <Navigation className="h-10 w-10 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Tempo Total</p>
                <p className="text-2xl font-bold text-gray-900">{formatarTempo(viagem.tempo_total_minutos)}</p>
              </div>
              <Clock className="h-10 w-10 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Visitas</p>
                <p className="text-3xl font-bold text-purple-600">{viagem.visitas?.length || 0}</p>
              </div>
              <User className="h-10 w-10 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Vel. Média</p>
                <p className="text-2xl font-bold text-gray-900">{viagem.velocidade_media?.toFixed(0) || 0} km/h</p>
              </div>
              <TrendingUp className="h-10 w-10 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mapa do Trajeto */}
      {mapaURL && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <MapPin className="h-5 w-5" />
              <span>Mapa do Trajeto</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-100 rounded-lg overflow-hidden">
              <img 
                src={mapaURL} 
                alt="Mapa do trajeto" 
                className="w-full h-auto"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/800x400?text=Erro+ao+carregar+mapa';
                }}
              />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                <span>Início</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                <span>Visitas</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                <span>Fim</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline da Viagem */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Timeline da Viagem</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Início */}
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <MapPin className="h-6 w-6 text-green-600" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-green-600">Viagem Iniciada</div>
                <div className="text-sm text-gray-600">{formatarData(viagem.data_hora_inicio)}</div>
                {viagem.endereco_inicio && (
                  <div className="text-xs text-gray-500 mt-1">{viagem.endereco_inicio}</div>
                )}
              </div>
            </div>

            {/* Visitas */}
            {viagem.visitas && viagem.visitas.map((visita, index) => (
              <div key={visita.id} className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-blue-600">Visita {index + 1}: {visita.nome_cliente || 'Cliente'}</div>
                  <div className="text-sm text-gray-600">
                    Chegada: {formatarData(visita.hora_chegada)}
                    {visita.hora_saida && ` • Saída: ${formatarData(visita.hora_saida)}`}
                  </div>
                  {visita.tempo_visita_minutos && (
                    <div className="text-xs text-gray-500">Duração: {formatarTempo(visita.tempo_visita_minutos)}</div>
                  )}
                  {visita.observacoes && (
                    <div className="text-sm text-gray-700 mt-1 bg-blue-50 p-2 rounded">{visita.observacoes}</div>
                  )}
                  {visita.fotos && visita.fotos.length > 0 && (
                    <div className="flex space-x-2 mt-2">
                      {visita.fotos.map((foto, i) => (
                        <div key={i} className="w-20 h-20 rounded border overflow-hidden">
                          <img src={foto} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Pausas */}
            {viagem.pausas && viagem.pausas.map((pausa, index) => (
              <div key={pausa.id || index} className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-yellow-600">Pausa</div>
                  <div className="text-sm text-gray-600">
                    {formatarData(pausa.hora_pausa)}
                    {pausa.hora_retomada && ` → ${formatarData(pausa.hora_retomada)}`}
                  </div>
                  {pausa.motivo && (
                    <div className="text-xs text-gray-500">Motivo: {pausa.motivo}</div>
                  )}
                  {pausa.tempo_pausa_minutos && (
                    <div className="text-xs text-gray-500">Duração: {formatarTempo(pausa.tempo_pausa_minutos)}</div>
                  )}
                </div>
              </div>
            ))}

            {/* Observações */}
            {viagem.observacoes && viagem.observacoes.map((obs, index) => (
              <div key={obs.id || index} className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-purple-600">Observação</div>
                  <div className="text-sm text-gray-600">{formatarData(obs.timestamp)}</div>
                  <div className="text-sm text-gray-700 mt-1 bg-purple-50 p-2 rounded">{obs.observacao}</div>
                </div>
              </div>
            ))}

            {/* Fim */}
            {viagem.data_hora_fim && (
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-red-600">Viagem Encerrada</div>
                  <div className="text-sm text-gray-600">{formatarData(viagem.data_hora_fim)}</div>
                  {viagem.endereco_fim && (
                    <div className="text-xs text-gray-500 mt-1">{viagem.endereco_fim}</div>
                  )}
                  {viagem.observacao_final && (
                    <div className="text-sm text-gray-700 mt-1 bg-red-50 p-2 rounded">{viagem.observacao_final}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Alertas */}
      {viagem.paradas_longas_nao_registradas > 0 && (
        <Card className="border-yellow-400">
          <CardHeader className="bg-yellow-50">
            <CardTitle className="flex items-center space-x-2 text-yellow-800">
              <AlertCircle className="h-5 w-5" />
              <span>Alertas</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="text-sm text-yellow-800">
              Esta viagem teve {viagem.paradas_longas_nao_registradas} parada(s) longa(s) não registrada(s) (mais de 1 hora parado).
            </div>
          </CardContent>
        </Card>
      )}

      {/* Informações Adicionais */}
      <Card>
        <CardHeader>
          <CardTitle>Informações Adicionais</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-semibold text-gray-700">Total de Pausas:</span>
              <span className="ml-2">{formatarTempo(viagem.total_pausas_minutos)}</span>
            </div>
            <div>
              <span className="font-semibold text-gray-700">Tempo Efetivo:</span>
              <span className="ml-2">{formatarTempo(viagem.tempo_efetivo_minutos)}</span>
            </div>
            <div>
              <span className="font-semibold text-gray-700">Pontos GPS Registrados:</span>
              <span className="ml-2">{viagem.trajeto?.length || 0}</span>
            </div>
            <div>
              <span className="font-semibold text-gray-700">Criado em:</span>
              <span className="ml-2">{formatarData(viagem.criado_em)}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DetalhesViagemPage;
